import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { AppContext } from '../../App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { 
  FaCalendar, FaUser, FaBox, FaEnvelope, FaPhone,
  FaCheckCircle, FaTimesCircle, FaComments, FaEuroSign
} from 'react-icons/fa';

export default function RentalRequests() {
  const { t, i18n } = useTranslation();
  const { API, user } = useContext(AppContext);
  const navigate = useNavigate();
  
  const [rentals, setRentals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRental, setSelectedRental] = useState(null);
  const [showResponseModal, setShowResponseModal] = useState(false);
  const [responseMessage, setResponseMessage] = useState('');

  useEffect(() => {
    if (!user || (!user.roles?.includes('supplier') && !user.roles?.includes('admin') && !user.roles?.includes('decouverte'))) {
      navigate('/');
      return;
    }
    fetchRentals();
  }, [user]);

  const fetchRentals = async () => {
    try {
      // Get all rentals where this user is the owner
      const response = await axios.get(`${API}/rentals?owner_id=${user.id}`);
      setRentals(response.data);
    } catch (error) {
      console.error('Error fetching rentals:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const updateRentalStatus = async (rentalId, status) => {
    try {
      await axios.put(`${API}/rentals/${rentalId}`, { status });
      toast.success('Statut mis à jour');
      fetchRentals();
    } catch (error) {
      console.error('Error updating rental:', error);
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleRespond = (rental) => {
    setSelectedRental(rental);
    setResponseMessage('');
    setShowResponseModal(true);
  };

  const sendResponse = async () => {
    if (!responseMessage.trim()) {
      toast.error('Veuillez saisir un message');
      return;
    }

    try {
      // Check if conversation exists
      const convsRes = await axios.get(`${API}/rentals/conversations/list?user_id=${user.id}`);
      let conversation = convsRes.data.find(c => 
        c.product_id === selectedRental.product_id && 
        c.renter_id === selectedRental.user_id
      );

      // Create conversation if doesn't exist
      if (!conversation) {
        const convRes = await axios.post(`${API}/rentals/conversations`, {
          product_id: selectedRental.product_id,
          renter_id: selectedRental.user_id,
          owner_id: user.id,
          initial_message: responseMessage
        });
        conversation = convRes.data;
      } else {
        // Send message to existing conversation
        await axios.post(
          `${API}/rentals/conversations/${conversation.id}/messages?user_id=${user.id}`,
          { content: responseMessage }
        );
      }

      toast.success('Réponse envoyée');
      setShowResponseModal(false);
      navigate(`/rentals/conversations/${conversation.id}`);
    } catch (error) {
      console.error('Error sending response:', error);
      toast.error('Erreur lors de l\'envoi');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-500',
      confirmed: 'bg-blue-500',
      in_progress: 'bg-purple-500',
      completed: 'bg-[#635BFF]',
      cancelled: 'bg-red-500'
    };
    return colors[status] || 'bg-gray-500';
  };

  const getStatusLabel = (status) => {
    const labels = {
      pending: 'En attente',
      confirmed: 'Confirmé',
      in_progress: 'En cours',
      completed: 'Terminé',
      cancelled: 'Annulé'
    };
    return labels[status] || status;
  };

  const filteredRentals = rentals.filter(rental => {
    if (filter !== 'all' && rental.status !== filter) return false;
    
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      return (
        rental.user_name?.toLowerCase().includes(searchLower) ||
        rental.user_email?.toLowerCase().includes(searchLower) ||
        rental.product_name?.toLowerCase().includes(searchLower)
      );
    }
    
    return true;
  });

  const stats = {
    total: rentals.length,
    pending: rentals.filter(r => r.status === 'pending').length,
    confirmed: rentals.filter(r => r.status === 'confirmed' || r.status === 'in_progress').length,
    revenue: rentals.filter(r => r.payment_status === 'paid').reduce((sum, r) => sum + r.total, 0)
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F5F5F5] flex items-center justify-center">
        <div className="text-xl text-[#0A2540]">{t('loading')}</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F5F5]">
      <div className="container mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-[#0A2540] mb-2">
            Demandes de Location Reçues
          </h1>
          <p className="text-gray-600">
            Gérez les demandes de location pour vos équipements
          </p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Total Demandes</p>
                  <p className="text-3xl font-bold text-[#0A2540]">{stats.total}</p>
                </div>
                <FaBox className="text-4xl text-blue-500" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">En Attente</p>
                  <p className="text-3xl font-bold text-yellow-600">{stats.pending}</p>
                </div>
                <FaCalendar className="text-4xl text-yellow-500" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Actives</p>
                  <p className="text-3xl font-bold text-[#635BFF]">{stats.confirmed}</p>
                </div>
                <FaCheckCircle className="text-4xl text-[#635BFF]" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Revenus</p>
                  <p className="text-3xl font-bold text-[#00D4FF]">{stats.revenue.toFixed(0)}€</p>
                </div>
                <FaEuroSign className="text-4xl text-[#00D4FF]" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Input
                placeholder="Rechercher (nom, email, produit...)"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              
              <Select value={filter} onValueChange={setFilter}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les statuts</SelectItem>
                  <SelectItem value="pending">En attente</SelectItem>
                  <SelectItem value="confirmed">Confirmé</SelectItem>
                  <SelectItem value="in_progress">En cours</SelectItem>
                  <SelectItem value="completed">Terminé</SelectItem>
                  <SelectItem value="cancelled">Annulé</SelectItem>
                </SelectContent>
              </Select>

              <Button 
                onClick={() => {
                  setFilter('all');
                  setSearchTerm('');
                }}
                variant="outline"
              >
                Réinitialiser
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Rentals List */}
        {filteredRentals.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <FaBox className="mx-auto text-6xl text-gray-300 mb-4" />
              <p className="text-gray-500 text-lg">
                {filter === 'all' ? 'Aucune demande de location reçue' : 'Aucune demande avec ce statut'}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {filteredRentals.map((rental) => (
              <Card key={rental.id} className="hover:shadow-md transition-all">
                <CardContent className="p-6">
                  <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-3">
                        <Badge className={`${getStatusColor(rental.status)} text-white`}>
                          {getStatusLabel(rental.status)}
                        </Badge>
                        {rental.status === 'pending' && (
                          <Badge className="bg-[#00D4FF] text-white">⚠️ Action requise</Badge>
                        )}
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm mb-3">
                        <div className="flex items-center gap-2">
                          <FaUser className="text-[#0A2540]" />
                          <span><strong>Client:</strong> {rental.user_name}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <FaBox className="text-[#00D4FF]" />
                          <span><strong>Produit:</strong> {rental.product_name}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <FaEnvelope className="text-blue-500" />
                          <span>{rental.user_email}</span>
                        </div>
                        {rental.user_phone && (
                          <div className="flex items-center gap-2">
                            <FaPhone className="text-[#635BFF]" />
                            <span>{rental.user_phone}</span>
                          </div>
                        )}
                        <div className="flex items-center gap-2">
                          <FaCalendar className="text-purple-500" />
                          <span>
                            {new Date(rental.start_date).toLocaleDateString('fr-FR')} → {new Date(rental.end_date).toLocaleDateString('fr-FR')}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <FaEuroSign className="text-[#635BFF]" />
                          <span><strong>Total:</strong> {rental.total}€</span>
                        </div>
                      </div>

                      {rental.notes && (
                        <div className="p-3 bg-gray-50 rounded mb-3">
                          <p className="text-sm text-gray-700">
                            <strong>Notes du client:</strong> {rental.notes}
                          </p>
                        </div>
                      )}

                      {rental.delivery_required && (
                        <div className="flex items-center gap-2 text-sm text-blue-600">
                          <span>🚚 Livraison demandée à: {rental.delivery_address}</span>
                        </div>
                      )}
                    </div>

                    <div className="flex flex-col gap-2">
                      <Button
                        size="sm"
                        onClick={() => handleRespond(rental)}
                        className="bg-[#0A2540] hover:bg-[#003A52]"
                      >
                        <FaComments className="mr-2" /> Répondre
                      </Button>

                      {rental.status === 'pending' && (
                        <>
                          <Button
                            size="sm"
                            onClick={() => updateRentalStatus(rental.id, 'confirmed')}
                            className="bg-[#635BFF] hover:bg-[#5248d9]"
                          >
                            <FaCheckCircle className="mr-2" /> Accepter
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => updateRentalStatus(rental.id, 'cancelled')}
                            className="text-red-600"
                          >
                            <FaTimesCircle className="mr-2" /> Refuser
                          </Button>
                        </>
                      )}

                      {rental.status === 'confirmed' && (
                        <Button
                          size="sm"
                          onClick={() => updateRentalStatus(rental.id, 'in_progress')}
                          className="bg-purple-600 hover:bg-purple-700"
                        >
                          Démarrer
                        </Button>
                      )}

                      {rental.status === 'in_progress' && (
                        <Button
                          size="sm"
                          onClick={() => updateRentalStatus(rental.id, 'completed')}
                          className="bg-blue-600 hover:bg-blue-700"
                        >
                          Terminer
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Response Modal */}
        <Dialog open={showResponseModal} onOpenChange={setShowResponseModal}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle className="text-2xl text-[#0A2540]">
                Répondre à la demande
              </DialogTitle>
            </DialogHeader>

            {selectedRental && (
              <div className="space-y-4">
                <div className="bg-gray-50 p-4 rounded">
                  <p className="text-sm text-gray-600 mb-1"><strong>Client:</strong> {selectedRental.user_name}</p>
                  <p className="text-sm text-gray-600 mb-1"><strong>Produit:</strong> {selectedRental.product_name}</p>
                  <p className="text-sm text-gray-600"><strong>Période:</strong> {new Date(selectedRental.start_date).toLocaleDateString('fr-FR')} → {new Date(selectedRental.end_date).toLocaleDateString('fr-FR')}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Votre message</label>
                  <Textarea
                    rows={6}
                    value={responseMessage}
                    onChange={(e) => setResponseMessage(e.target.value)}
                    placeholder="Bonjour,

Merci pour votre demande de location. Je confirme la disponibilité de l'équipement pour les dates demandées.

Cordialement"
                  />
                </div>

                <div className="flex gap-3 justify-end">
                  <Button
                    variant="outline"
                    onClick={() => setShowResponseModal(false)}
                  >
                    Annuler
                  </Button>
                  <Button
                    onClick={sendResponse}
                    className="bg-[#00D4FF] hover:bg-[#00B4D8]"
                  >
                    <FaComments className="mr-2" /> Envoyer le message
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
